<template>
  <flash-message v-if="failed" state="error">
    {{ $t("customFlows.newFlowCreated.error") }}
  </flash-message>
  <Modal :active="true" @close="emits('close')" data-test="create-flow-modal">
    <template #header>{{ $t("customFlows.newFlowCreated.header") }}</template>
    <template #body>
      <div>
        <label class="label" for="new-flow-title">{{
          $t("customFlows.newFlowCreated.inputLabel")
        }}</label>
        <input
          id="new-flow-title"
          class="input"
          type="text"
          v-model="title"
          ref="inputTitle"
          @keydown.enter="onTitleEnter"
        />
      </div>
    </template>
    <template #footer>
      <div class="buttons">
        <button
          class="button primary"
          :disabled="!title.trim() || pending"
          @click="submit"
        >
          <span class="material-symbols-outlined">save</span>
          {{ $t("customFlows.newFlowCreated.submit") }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { Modal, FlashMessage } from "@/components";
import { onMounted, ref } from "vue";
import { useCreateCustomFlowMutation } from "@/graphql/operations";

const emits = defineEmits(["close", "success"]);
const title = ref("");
const pending = ref(false);
const failed = ref(false);
const inputTitle = ref<HTMLInputElement>();
const { executeMutation } = useCreateCustomFlowMutation();
const submit = async () => {
  if (!title.value.trim() || pending.value) return;
  pending.value = true;
  failed.value = false;
  try {
    const result = await executeMutation({ title: title.value.trim() });
    const id = result.data?.createFlow?.flow?.id;
    if (result.error || !id) {
      failed.value = true;
      return;
    }
    emits("success", id);
    emits("close");
  } catch {
    failed.value = true;
  } finally {
    pending.value = false;
  }
};
onMounted(() => inputTitle.value?.focus());
const onTitleEnter = (event: KeyboardEvent) => {
  if (event.isComposing || event.repeat) return;
  event.preventDefault();
  void submit();
};
</script>

<style scoped>
label {
  font-size: 0.9em;
  display: block;
  line-height: 2em;
}
</style>
